import React, { createContext, useEffect, useState, useCallback, useRef } from "react";
import wsManager from "api/ws/websocketManager";
import { generateMessageHash } from "context/messageHash";
import { createWsUrlFactory } from "context/wsUrlFactory";
import { getAuthTokenFromLocalStorage } from "hooks/useAuthToken";
import { useLocalStorage } from "hooks/useLocalStorage";
import { CLIENT_ID_LOCAL_STORAGE_KEY } from "utils/constants";
import { httpStatusOf } from "utils/httpStatusOf";
import { reportError } from "utils/sentry/reportError";

/**
 * The session check the WS factory uses: the same `token_control` call the router makes at boot. It is
 * read-only on the BE and answers HTTP 401 for an expired token, which the shared interceptor turns into
 * "clear token + logout" — so a dead session ends exactly like it does for any other request.
 *
 * Imported lazily on purpose: many modules import this file only for the context object, and a static
 * import would make every one of them construct the whole HTTP client at module load.
 */
const verifySession = async () => {
  const { default: userService } = await import("api/user/user.service");

  return userService.tokenControl({ client_id: useLocalStorage().getItem(CLIENT_ID_LOCAL_STORAGE_KEY) });
};

/** Decides whether a subscriber receives an admitted frame. */
export type SocketFrameFilter = (frame: any) => boolean;
/** Called once per admitted frame that passes its filter, synchronously at admission. */
export type SocketFrameHandler = (frame: any) => void;

interface FrameSubscription {
  filter: SocketFrameFilter;
  handler: SocketFrameHandler;
  reported: boolean;
}

interface SocketContextType {
  sendMessage: (data: any) => void;
  lastMessage: any;
  isConnected: boolean;
  reconnect: () => void;
  ping: () => void;
  isMessageProcessed: (messageId: string) => boolean;
  markMessageAsProcessed: (messageId: string) => void;
  setSessionId: (sessionId: string) => void;
  getSessionId: () => string | null;
  /** Mark a design_id as completed (via polling or socket) to prevent double-handling */
  markDesignCompleted: (designId: string) => void;
  /** Check if a design_id was already completed */
  isDesignCompleted: (designId: string) => boolean;
  /**
   * REF-6876 (D2): per-frame delivery. `handler` runs once for EVERY admitted frame that passes `filter`,
   * inline at admission (after keep_alive handling and the hash dedup, before `setLastMessage`), outside
   * React rendering — so frames that React would batch into one `lastMessage` commit are never collapsed.
   * No dedup of its own, no replay, no ordering beyond the order this tab admits frames. Call it from an
   * effect and return the unsubscribe from that effect's cleanup.
   */
  subscribe: (filter: SocketFrameFilter, handler: SocketFrameHandler) => () => void;
  /**
   * REF-6876 (D2): the latest admitted frame — the frame `lastMessage` holds once React commits — or null.
   * Read synchronously, so inside a batch it may already be ahead of the rendered `lastMessage`. One frame,
   * not a log: a consumer that becomes eligible late reads it from its own effect and applies its own filter.
   */
  getRetained: () => any;
}

export const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const SocketProvider = ({ children, token, secret, frontendKey }: { children: React.ReactNode; token: string; secret: string; frontendKey: string }) => {
  const [lastMessage, setLastMessage] = useState<any>(null);
  const [isConnected, setIsConnected] = useState(false);
  const processedMessages = useRef<Set<string>>(new Set());
  const messageHashMap = useRef<Map<string, boolean>>(new Map());
  const completedDesigns = useRef<Set<string>>(new Set());
  const subscriptions = useRef<Set<FrameSubscription>>(new Set());
  const retained = useRef<any>(null);

  async function generateSignature(message: string, secret: string): Promise<string> {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey("raw", enc.encode(secret), { name: "HMAC", hash: "SHA-256" }, false, ["sign"]);

    const signature = await crypto.subtle.sign("HMAC", key, enc.encode(message));

    return Array.from(new Uint8Array(signature))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  const connectWebSocket = useCallback(async () => {
    try {
      // REF-6539: hand the manager a URL FACTORY, not a url. Every (re)connect attempt re-reads the
      // current token so a rotated credential is picked up; a missing token (logged out) returns null
      // and stops the reconnect loop. An expired token is checked with the server first (see
      // createWsUrlFactory) instead of being retried against the WS endpoint forever.
      const buildUrl = createWsUrlFactory({
        // No fallback to the token captured at mount: if storage has no token the user is logged out.
        getToken: getAuthTokenFromLocalStorage,
        sign: (currentToken) => generateSignature(`${frontendKey}:${currentToken}`, secret),
        host: process.env.REACT_APP_API_URL_CLEAN as string,
        verifySession,
      });

      await wsManager.connect(buildUrl);
    } catch (error) {
      console.error("[WebSocket] Failed to connect", { status: httpStatusOf(error) });
      throw error;
    }
  }, [token, secret, frontendKey]);

  const ping = useCallback(() => {
    wsManager.send({ type: "ping" });
  }, []);

  const handleIncomingMessage = useCallback((message: any) => {
    if (!message) return;

    // Handle keep_alive ping from server
    if (message.type === "keep_alive") {
      if (process.env.NODE_ENV === "development") console.log("[WebSocket] Received keep_alive, sending keepalive_ack");

      wsManager.send({ type: "keepalive_ack" });

      return;
    }

    const messageHash = generateMessageHash(message);

    if (messageHashMap.current.has(messageHash)) {
      console.log("[WebSocket] Duplicate message detected, skipping", {
        hash: messageHash,
        design_id: message?.design_id,
        project_id: message?.project_id,
        design_type: message?.design_type,
        type: message?.type,
        status: message?.status,
        data_length: Array.isArray(message?.data) ? message.data.length : undefined,
        first_data_id: Array.isArray(message?.data) ? message.data[0]?.id : undefined,
      });

      return;
    }

    messageHashMap.current.set(messageHash, true);

    // Keep processed messages for longer to prevent re-processing
    setTimeout(() => {
      messageHashMap.current.delete(messageHash);
    }, 60000); // Keep for 1 minute

    // Signal B — delivery_ack (Perceived Latency handover). Confirm a *live* WS delivery
    // of a terminal design event over the same socket so the backend can tell socket
    // delivery apart from slow poll fallback. Keyed on design_id (the join key); training
    // events (train_id/training_id, no design_id) are naturally excluded. Fire-and-forget:
    // no response, duplicates harmless (BE keeps the first). The BE tags frames redelivered
    // from the reconnect replay stream with `replayed: true`; undefined on live pushes → false.
    const isSuccessTerminal = message.status === 1 && (message.type === "success" || message.type === "completed");
    const isErrorTerminal = message.status === 99 || message.type === "failed" || message.type === "error";
    if (message.design_id && (isSuccessTerminal || isErrorTerminal)) {
      wsManager.send({ type: "delivery_ack", design_id: message.design_id, replayed: message.replayed === true });
    }

    const admitted = { ...message, messageHash };
    retained.current = admitted;
    // A snapshot, so a handler that (un)subscribes does not change who receives THIS frame — except that a
    // subscription removed during the dispatch receives nothing from that point on.
    Array.from(subscriptions.current).forEach((subscription) => {
      if (!subscriptions.current.has(subscription)) return;
      try {
        if (subscription.filter(admitted)) subscription.handler(admitted);
      } catch {
        // Isolated: a throwing subscriber blocks neither the others nor `lastMessage`. Reported once per
        // subscription, never with the frame or the thrown value (either can carry the payload).
        if (!subscription.reported) {
          subscription.reported = true;
          try {
            reportError(new Error("WebSocket frame subscriber threw"), {
              source: "websocket",
              featureArea: "unknown",
              fingerprint: ["ws-subscriber-threw"],
            });
          } catch {
            // Telemetry must never skip the remaining subscribers or `setLastMessage`.
          }
        }
      }
    });

    setLastMessage(admitted);
  }, []);

  const subscribe = useCallback((filter: SocketFrameFilter, handler: SocketFrameHandler) => {
    const subscription: FrameSubscription = { filter, handler, reported: false };
    subscriptions.current.add(subscription);

    return () => {
      subscriptions.current.delete(subscription);
    };
  }, []);

  const getRetained = useCallback(() => retained.current, []);

  useEffect(() => {
    if (!token || !secret || !frontendKey) {
      console.log("[WebSocket] Missing required credentials");
      // REF-6539: credentials gone (logout / auth lost) → stop any reconnect loop instead of retrying a
      // dead token. A later login re-runs this effect with a token and reconnects.
      wsManager.disconnect();

      return;
    }

    // Set up listeners
    const unsubscribeMessage = wsManager.addMessageListener(handleIncomingMessage);
    const unsubscribeConnection = wsManager.addConnectionStateListener(setIsConnected);

    // Connect
    connectWebSocket()
      .then(() => {
        console.log("[WebSocket] Initial connection attempt completed");
      })
      .catch((error) => {
        console.error("[WebSocket] Failed to connect", { status: httpStatusOf(error) });
      });

    return () => {
      unsubscribeMessage();
      unsubscribeConnection();
      // Note: We don't disconnect here as other components might still need the connection
      // The connection will be managed globally by the WebSocketManager
    };
  }, [token, secret, frontendKey, connectWebSocket, handleIncomingMessage]);

  const reconnect = useCallback(() => {
    if (wsManager.isConnected()) {
      console.log("[WebSocket] Already connected, skipping manual reconnect");

      return;
    }
    console.log("[WebSocket] Manual reconnect triggered");
    connectWebSocket().catch((error) => {
      console.error("[WebSocket] Failed to manually reconnect", { status: httpStatusOf(error) });
    });
  }, [connectWebSocket]);

  const sendMessage = useCallback((data: any) => {
    wsManager.send(data);
  }, []);

  const isMessageProcessed = useCallback((messageId: string) => {
    return processedMessages.current.has(messageId);
  }, []);

  const markMessageAsProcessed = useCallback((messageId: string) => {
    processedMessages.current.add(messageId);
  }, []);

  const markDesignCompleted = useCallback((designId: string) => {
    completedDesigns.current.add(designId);
    // Auto-cleanup after 5 minutes to prevent memory buildup
    setTimeout(() => {
      completedDesigns.current.delete(designId);
    }, 5 * 60 * 1000);
  }, []);

  const isDesignCompleted = useCallback((designId: string) => {
    return completedDesigns.current.has(designId);
  }, []);

  const setSessionId = useCallback((newSessionId: string) => {
    wsManager.setSessionId(newSessionId);
  }, []);

  const getSessionId = useCallback(() => {
    return wsManager.getSessionId();
  }, []);

  return (
    <SocketContext.Provider
      value={{
        sendMessage,
        lastMessage,
        isConnected,
        reconnect,
        ping,
        isMessageProcessed,
        markMessageAsProcessed,
        markDesignCompleted,
        isDesignCompleted,
        setSessionId,
        getSessionId,
        subscribe,
        getRetained,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};
